import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, type Editor } from '@tiptap/react';
import { closeHistory } from '@tiptap/pm/history';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useParams, Link } from 'react-router-dom';
import { PAGE_SIZES } from 'tiptap-pagination-plus';

import type { ZoomSetting } from './components/TiptapDocumentCanvas';
import DocumentEditorShell from './components/DocumentEditorShell';
import { createDocumentEditorExtensions, USE_STANDARD_TABLE } from './documentEditorExtensions';
import { useGetResourceByIdQuery, useUpdateResourceMutation } from './resourcesApi';
import { type DocumentImagePlaceholderUiActions } from './components/DocumentImagePlaceholderExtension';
import UnsplashImageLibraryDialog, {
  type UnsplashImageSelection,
} from './components/UnsplashImageLibraryDialog';
import { usePendingDocumentImages } from './hooks/usePendingDocumentImages';
import { useGoogleDocsExport } from './hooks/useGoogleDocsExport';

import type { AgentContext } from '@/shared/types/chat';
import {
  asDocumentContent,
  createEmptyDocumentContent,
  sanitizeTiptapDoc,
  stripTransientStorageImageUrlsFromTiptapDoc,
  type DocumentContentV1,
  type TiptapDocNode,
} from '@/shared/types/resource';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';
import { useConfirm } from '@/shared/components/ConfirmProvider';
import {
  closeScopedAiPanel,
  setScopedAiContext,
  toggleScopedAiPanel,
} from '@/shared/store/uiSlice';
import { useObjectPermissionState } from '@/shared/hooks/useCanEdit';
import { PageSpinner } from '@/shared/components/PageSpinner';
import {
  addAiDraftEditorFlushListener,
  addAiDraftResourceAppliedListener,
} from '@/features/chatbot/aiDraftApplyEvents';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { ScoperAiDialog } from '@/features/chatbot/scoper-dev';
import { getLiveEditor } from '@/shared/utils/tiptapEditor';

type PageSize = 'letter' | 'a4';
type PageMargins = 'normal' | 'narrow' | 'wide';

const MARGIN_SCALE: Record<PageMargins, number> = {
  narrow: 0.5,
  normal: 1,
  wide: 1.35,
};

const EDITOR_PAGE_GAP = 20;
const PRINT_PAGE_GAP = 0;
const EDITOR_PAGE_BREAK_BACKGROUND = '#f8f9fa';
const PRINT_PAGE_BREAK_BACKGROUND = '#ffffff';

function replaceEditorContent(editor: Editor, doc: TiptapDocNode, addToHistory: boolean) {
  const nextDocument = editor.schema.nodeFromJSON(doc as unknown as Record<string, unknown>);
  const tr = editor.state.tr.replaceWith(0, editor.state.doc.content.size, nextDocument.content);
  tr.setMeta('addToHistory', addToHistory);
  editor.view.dispatch(closeHistory(tr));
}

const ResourceDocumentEditorPage: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { user } = useAppSelector((state) => state.auth);
  const confirm = useConfirm();
  const resourcePermission = useObjectPermissionState('resource', resourceId, 'editor');
  const canEdit = resourcePermission.isAllowed;
  const isPermissionLoading = resourcePermission.isLoading;
  const {
    data: resource,
    isLoading,
    error,
  } = useGetResourceByIdQuery(resourceId!, { skip: !resourceId });
  const [updateResource, { isLoading: isSaving }] = useUpdateResourceMutation();
  const [title, setTitle] = useState('');
  const latestTitleRef = useRef(title);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [docxError, setDocxError] = useState<string | null>(null);
  const [googleExportError, setGoogleExportError] = useState<string | null>(null);
  const [isDocxExporting, setIsDocxExporting] = useState(false);
  const [isGoogleExporting, setIsGoogleExporting] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const libraryPlaceholderKeyRef = useRef<string | null>(null);
  const suppressUpdateRef = useRef(false);
  const lastLoadedResourceRef = useRef<string | null>(null);
  const placeholderUiActionsRef = useRef<DocumentImagePlaceholderUiActions | null>(null);
  const pendingAiAppliedDraftRef = useRef<string | null>(null);
  const docContent = useMemo(() => (resource ? asDocumentContent(resource) : null), [resource]);
  const docContentVersion = resource?.version ?? 0;
  const pageSize: PageSize = docContent?.editorSettings.pageSize ?? 'letter';
  const margins: PageMargins = docContent?.editorSettings.margins ?? 'narrow';
  const pageLayout = useMemo(() => getPaginationLayout(pageSize, margins), [pageSize, margins]);
  const printPageMargin = useMemo(() => {
    const tb = `${(pageLayout.marginTop / 96).toFixed(4)}in`;
    const lr = `${(pageLayout.marginLeft / 96).toFixed(4)}in`;
    return tb === lr ? tb : `${tb} ${lr}`;
  }, [pageLayout]);
  const { exportToGoogle } = useGoogleDocsExport({ onError: setGoogleExportError });
  const scoperEnabled = import.meta.env.DEV && isFeatureEnabled('scoperAiDevDialog');
  const [scoperOpen, setScoperOpen] = useState(false);
  const isMediumOrSmallerScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const userSelectedZoomRef = useRef(false);
  const [zoomSetting, setZoomSetting] = useState<ZoomSetting>(() =>
    isMediumOrSmallerScreen ? 'fit-width' : 100
  );

  const handleZoomChange = useCallback((next: ZoomSetting) => {
    userSelectedZoomRef.current = true;
    setZoomSetting(next);
  }, []);

  useEffect(() => {
    if (userSelectedZoomRef.current) {
      return;
    }
    setZoomSetting(isMediumOrSmallerScreen ? 'fit-width' : 100);
  }, [isMediumOrSmallerScreen]);

  useEffect(() => {
    latestTitleRef.current = title;
  }, [title]);

  const editor = useEditor(
    {
      extensions: createDocumentEditorExtensions({
        getPlaceholderUiActions: () => placeholderUiActionsRef.current,
        pagination: {
          ...pageLayout,
          pageGap: EDITOR_PAGE_GAP,
          pageGapBorderSize: 0,
          pageGapBorderColor: EDITOR_PAGE_BREAK_BACKGROUND,
          pageBreakBackground: EDITOR_PAGE_BREAK_BACKGROUND,
          headerLeft: '',
          headerRight: '',
          footerLeft: '',
          footerRight: '',
          contentMarginTop: 0,
          contentMarginBottom: 0,
        },
      }),
      content: createEmptyDocumentContent().tiptapDoc as any,
      editorProps: {
        attributes: {
          spellcheck: 'true',
          class: 'google-docs-editor',
        },
      },
      onUpdate: () => {
        if (suppressUpdateRef.current) return;
        setDirty(true);
      },
      shouldRerenderOnTransaction: false,
    },
    [resource?.id, pageSize, margins]
  );
  const {
    items: pendingImageItems,
    count: pendingImageCount,
    refresh: refreshPendingImages,
    focusPlaceholder,
    resolvePlaceholder,
  } = usePendingDocumentImages(editor);

  const scopedLevel: 'course' | 'unit' | 'lesson' | undefined = resource?.parentLessonId
    ? 'lesson'
    : resource?.parentUnitId
      ? 'unit'
      : resource?.parentCourseId
        ? 'course'
        : undefined;
  const scoperContext = useMemo<AgentContext | null>(() => {
    if (!resource?.courseId) return null;

    return {
      assistantType: 'course',
      courseId: resource.courseId,
      focus: {
        level: scopedLevel ?? 'course',
        courseId: resource.courseId,
        unitId: resource.parentUnitId ?? undefined,
        lessonId: resource.parentLessonId ?? undefined,
        resourceId: resource.id,
        resourceKind: resource.resourceKind,
      },
      profile: resource.resourceKind === 'document' ? 'document' : undefined,
    };
  }, [resource, scopedLevel]);

  // Track the server version we last loaded so we can detect AI-applied changes
  const lastLoadedVersionRef = useRef<number>(0);
  const lastLoadedUpdatedAtRef = useRef<string | null>(null);
  // Track which editor instance we last loaded content into
  const lastLoadedEditorRef = useRef<Editor | null>(null);

  useEffect(() => {
    const instance = getLiveEditor(editor);
    if (!resource || !instance || !docContent) return;

    // If the editor instance changed (e.g. from page size change), we must re-load strict
    const editorChanged = lastLoadedEditorRef.current !== instance;
    const isInitialLoad = lastLoadedResourceRef.current !== resource.id || editorChanged;
    const resourceUpdatedAt = resource.updatedAt ?? null;
    const isServerUpdate =
      !isInitialLoad &&
      !dirty &&
      (docContentVersion > lastLoadedVersionRef.current ||
        isLaterTimestamp(resourceUpdatedAt, lastLoadedUpdatedAtRef.current));

    if (!isInitialLoad && !isServerUpdate) return;

    const loadContent = () => {
      const nextTitle = docContent.title || resource.title;
      const nextDoc = sanitizeTiptapDoc(docContent.tiptapDoc);
      const isAiAppliedUpdate = isServerUpdate && pendingAiAppliedDraftRef.current !== null;

      lastLoadedResourceRef.current = resource.id;
      lastLoadedVersionRef.current = docContentVersion;
      lastLoadedUpdatedAtRef.current = resourceUpdatedAt;
      lastLoadedEditorRef.current = instance;
      suppressUpdateRef.current = true;
      latestTitleRef.current = nextTitle;
      setTitle(nextTitle);
      // Use addToHistory for AI-applied updates so Ctrl+Z undoes the whole AI change
      replaceEditorContent(instance, nextDoc, isAiAppliedUpdate);
      if (isAiAppliedUpdate) {
        pendingAiAppliedDraftRef.current = null;
      }

      refreshPendingImages();
      setDirty(false);
      setSaveError(null);
      setDocxError(null);
      window.setTimeout(() => {
        suppressUpdateRef.current = false;
      }, 0);
    };

    if (instance.isInitialized) {
      loadContent();
      return;
    }

    const handleCreate = () => {
      instance.off('create', handleCreate);
      loadContent();
    };

    instance.on('create', handleCreate);

    return () => {
      instance.off('create', handleCreate);
    };
  }, [resource?.id, editor, docContent, docContentVersion, dirty, refreshPendingImages]);

  // -- sync editable state when canEdit changes after initial load -----------
  useEffect(() => {
    const instance = getLiveEditor(editor);
    if (!instance) return;
    instance.setEditable(canEdit);
  }, [editor, canEdit]);

  const persistDocument = useCallback(
    async (force = false) => {
      if (!canEdit) return false;
      const instance = getLiveEditor(editor);
      if (!resource || !instance || !docContent) return false;
      if (!force && !dirty) return false;

      const preSaveDoc = instance.getJSON();
      const preSaveRawContent = JSON.stringify(preSaveDoc);
      const preSaveTitle = title || 'Untitled Document';
      const nextDoc: DocumentContentV1 = {
        ...docContent,
        title: preSaveTitle,
        tiptapDoc: stripTransientStorageImageUrlsFromTiptapDoc(
          sanitizeTiptapDoc(preSaveDoc as any)
        ),
      };

      let savedResource: NonNullable<typeof resource>;
      const newVersion = (resource.version ?? 1) + 1;

      try {
        setSaveError(null);
        savedResource = await updateResource({
          id: resource.id,
          title: preSaveTitle,
          content: nextDoc,
          version: newVersion,
        }).unwrap();
      } catch (errorSaving) {
        console.error('Failed to save resource document', errorSaving);
        setSaveError('Unable to save changes. Keep this tab open and try again.');
        return false;
      }

      if (instance.isDestroyed) {
        return true;
      }

      // Update refs to prevent useEffect from reloading content we just saved
      lastLoadedVersionRef.current = newVersion;
      if (resource.id) {
        lastLoadedResourceRef.current = resource.id;
      }
      lastLoadedUpdatedAtRef.current = savedResource.updatedAt ?? null;

      try {
        // Only clear dirty if neither editor content nor title changed while the save was in flight.
        const currentContent = JSON.stringify(instance.getJSON());
        const currentTitle = latestTitleRef.current || 'Untitled Document';

        if (currentContent === preSaveRawContent && currentTitle === preSaveTitle) {
          setDirty(false);
        }
      } catch (errorPostSave) {
        console.warn(
          'Saved resource document, but skipped local dirty-state reconciliation',
          errorPostSave
        );
      }

      return true;
    },
    [canEdit, resource, editor, docContent, dirty, title, updateResource]
  );

  useEffect(() => {
    if (!resourceId) return;

    return addAiDraftResourceAppliedListener((detail) => {
      if (detail.resourceIds.includes(resourceId)) {
        pendingAiAppliedDraftRef.current = detail.draftId;
      }
    });
  }, [resourceId]);

  useEffect(() => {
    if (!canEdit || !resourceId) return;

    return addAiDraftEditorFlushListener((detail) => {
      if (detail.resourceId !== resourceId || detail.resourceKind !== 'document') return;
      detail.promises.push(dirty ? persistDocument(true) : Promise.resolve(true));
    });
  }, [canEdit, dirty, persistDocument, resourceId]);

  const handleExportDocx = useCallback(() => {
    const instance = getLiveEditor(editor);
    if (!instance) return;

    setDocxError(null);
    setIsDocxExporting(true);

    const fileName = `${toSafeFilename(title || resource?.title || 'document')}.docx`;
    let completed = false;

    const finish = () => {
      if (completed) return;
      completed = true;
      setIsDocxExporting(false);
    };

    try {
      const maybeResult = instance.commands.exportDocx({
        exportType: 'blob',
        customNodes: [],
        styleOverrides: {
          paragraphStyles: [
            { id: 'Heading1', paragraph: { spacing: { before: 0 } } },
            { id: 'Heading2', paragraph: { spacing: { before: 0 } } },
            { id: 'Heading3', paragraph: { spacing: { before: 0 } } },
            { id: 'Heading4', paragraph: { spacing: { before: 0 } } },
            { id: 'Heading5', paragraph: { spacing: { before: 0 } } },
            { id: 'Heading6', paragraph: { spacing: { before: 0 } } },
          ],
        },
        onCompleteExport: (result) => {
          try {
            if (!(result instanceof Blob)) {
              setDocxError('DOCX export returned an unexpected file format.');
              return;
            }

            const exportUrl = URL.createObjectURL(result);
            const anchor = document.createElement('a');
            anchor.href = exportUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.setTimeout(() => URL.revokeObjectURL(exportUrl), 0);
          } catch (errorExport) {
            console.error('Failed to export DOCX', errorExport);
            setDocxError('Unable to export DOCX. Try again.');
          } finally {
            finish();
          }
        },
      });

      if (maybeResult === false) {
        setDocxError('Unable to export DOCX from the current editor state.');
        finish();
        return;
      }

      if (isPromiseLike(maybeResult)) {
        void maybeResult.catch((errorExport) => {
          console.error('Failed to export DOCX', errorExport);
          setDocxError('Unable to export DOCX. Try again.');
          finish();
        });
      }
    } catch (errorExport) {
      console.error('Failed to export DOCX', errorExport);
      setDocxError('Unable to export DOCX. Try again.');
      finish();
    }
  }, [editor, resource?.title, title]);

  const applyEditorPrintLayout = useCallback(() => {
    const instance = getLiveEditor(editor);
    if (!instance) return;

    instance
      .chain()
      .focus()
      .updatePageGap(PRINT_PAGE_GAP)
      .updatePageBreakBackground(PRINT_PAGE_BREAK_BACKGROUND)
      .run();
  }, [editor]);

  const restoreEditorLayout = useCallback(() => {
    const instance = getLiveEditor(editor);
    if (!instance) return;

    instance
      .chain()
      .focus()
      .updatePageGap(EDITOR_PAGE_GAP)
      .updatePageBreakBackground(EDITOR_PAGE_BREAK_BACKGROUND)
      .run();
  }, [editor]);

  const handlePrintDocument = useCallback(async () => {
    if (pendingImageCount > 0) {
      const ok = await confirm({
        title: 'Print with pending images?',
        message: `${pendingImageCount} pending image placeholder${pendingImageCount === 1 ? '' : 's'} will print as placeholder content until resolved. Print anyway?`,
        confirmButtonText: 'Print anyway',
      });
      if (!ok) return;
    }

    applyEditorPrintLayout();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  }, [applyEditorPrintLayout, confirm, pendingImageCount]);

  const handleExportToGoogleDocs = useCallback(async () => {
    if (!resource || resource.resourceKind !== 'document') return;

    const unresolvedCount = refreshPendingImages().length;
    if (unresolvedCount > 0) {
      const ok = await confirm({
        title: 'Export with pending images?',
        message: `${unresolvedCount} image placeholder${unresolvedCount === 1 ? '' : 's'} will appear as "[Image: search term]" text in the exported Google Doc. Export anyway?`,
        confirmButtonText: 'Export anyway',
      });

      if (!ok) return;
    }

    if (canEdit && dirty) {
      const saved = await persistDocument(true);
      if (!saved) return;
    }

    setIsGoogleExporting(true);
    try {
      await exportToGoogle(resource);
    } finally {
      setIsGoogleExporting(false);
    }
  }, [canEdit, confirm, dirty, exportToGoogle, persistDocument, refreshPendingImages, resource]);

  const handlePendingImagesPrimaryAction = useCallback(() => {
    if (pendingImageCount === 0) return;
    const firstItem = pendingImageItems[0];
    if (firstItem) focusPlaceholder(firstItem.key);
  }, [focusPlaceholder, pendingImageCount, pendingImageItems]);

  const openPlaceholderLibrary = useCallback(
    (key: string, searchTerm?: string) => {
      libraryPlaceholderKeyRef.current = key;
      const term =
        searchTerm ?? pendingImageItems.find((item) => item.key === key)?.imageQuery ?? '';
      setLibrarySearchTerm(term);
      setLibraryOpen(true);
    },
    [pendingImageItems]
  );

  const handleResolvePendingImageFromLibrary = useCallback(
    (selection: UnsplashImageSelection) => {
      const key = libraryPlaceholderKeyRef.current;
      setLibraryOpen(false);
      libraryPlaceholderKeyRef.current = null;
      if (!key) return;
      resolvePlaceholder(key, selection);
    },
    [resolvePlaceholder]
  );

  const handleCloseLibrary = useCallback(() => {
    setLibraryOpen(false);
    libraryPlaceholderKeyRef.current = null;
  }, []);

  useEffect(() => {
    placeholderUiActionsRef.current = canEdit
      ? {
          onResolvePlaceholder: (key, selection) => {
            resolvePlaceholder(key, selection);
          },
          onBrowseLibrary: (key, searchTerm) => {
            openPlaceholderLibrary(key, searchTerm);
          },
        }
      : null;
  }, [canEdit, openPlaceholderLibrary, resolvePlaceholder]);

  useEffect(() => {
    if (isPermissionLoading) return;
    if (!canEdit) {
      dispatch(closeScopedAiPanel());
      return;
    }
    if (!resource) return;
    dispatch(
      setScopedAiContext({
        level: scopedLevel ?? 'course',
        courseId: resource.courseId,
        unitId: resource.parentUnitId ?? undefined,
        lessonId: resource.parentLessonId ?? undefined,
        resourceId: resource.id,
        resourceKind: resource.resourceKind,
      })
    );
  }, [canEdit, isPermissionLoading, dispatch, resource, scopedLevel]);

  useEffect(() => {
    if (!canEdit) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [canEdit, dirty]);

  useEffect(() => {
    if (!canEdit || !resource || !getLiveEditor(editor) || !docContent || !dirty) return;

    const handle = window.setTimeout(() => {
      void persistDocument(false);
    }, 900);

    return () => window.clearTimeout(handle);
  }, [canEdit, dirty, resource?.id, editor, docContent, persistDocument]);

  useEffect(() => {
    const handleBeforePrint = () => {
      applyEditorPrintLayout();
    };

    const handleAfterPrint = () => {
      restoreEditorLayout();
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [applyEditorPrintLayout, restoreEditorLayout]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();

      if (key === 's' && canEdit) {
        event.preventDefault();
        void persistDocument(true);
        return;
      }

      if (key === 'p' && pendingImageCount > 0) {
        event.preventDefault();
        void handlePrintDocument();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canEdit, handlePrintDocument, pendingImageCount, persistDocument]);

  if (isLoading) return <PageSpinner />;
  if (error || !resource) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity={!user ? 'warning' : 'error'}>
          {!user ? (
            <>
              This content may be private. <Link to="/login">Sign in</Link> to access it if you have
              permission.
            </>
          ) : (
            'Unable to load the document resource.'
          )}
        </Alert>
      </Box>
    );
  }

  if (resource.resourceKind !== 'document') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">This resource is not a document.</Alert>
      </Box>
    );
  }

  return (
    <Box className="document-editor-page" sx={{ py: 1.5 }}>
      <Container maxWidth={false}>
        <Box
          className="document-editor-topbar"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
            gap: 1,
          }}
        >
          <Box>
            <TextField
              label="Title"
              fullWidth
              size="small"
              sx={{ mb: 0.5, maxWidth: 540, minWidth: 375 }}
              value={title}
              onChange={(event) => {
                const nextTitle = event.target.value;
                latestTitleRef.current = nextTitle;
                setTitle(nextTitle);
                setDirty(true);
              }}
              slotProps={{ input: { readOnly: !canEdit } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!canEdit && <Chip label="View only" size="small" variant="outlined" />}
            {canEdit && (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => dispatch(toggleScopedAiPanel())}
                >
                  AI CoPlanner
                </Button>
                {scoperEnabled && scoperContext && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setScoperOpen(true)}
                    sx={{ borderStyle: 'dashed' }}
                  >
                    Dev AI
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
        {saveError && (
          <Alert className="document-editor-save-error" severity="error" sx={{ mb: 1.5 }}>
            {saveError}
          </Alert>
        )}
        {docxError && (
          <Alert className="document-editor-docx-error" severity="error" sx={{ mb: 1.5 }}>
            {docxError}
          </Alert>
        )}
        {googleExportError && (
          <Alert className="document-editor-google-export-error" severity="error" sx={{ mb: 1.5 }}>
            {googleExportError}
          </Alert>
        )}
        <DocumentEditorShell
          editor={editor}
          saving={isSaving}
          dirty={dirty}
          canEdit={canEdit}
          docxExporting={isDocxExporting}
          googleExporting={isGoogleExporting}
          pendingImageCount={pendingImageCount}
          pageSize={pageSize}
          printPageMargin={printPageMargin}
          useStandardTable={USE_STANDARD_TABLE}
          zoomSetting={zoomSetting}
          onZoomChange={handleZoomChange}
          onPrintNow={handlePrintDocument}
          onDocxExport={handleExportDocx}
          onGoogleExport={user ? handleExportToGoogleDocs : undefined}
          onPendingImagesRefresh={refreshPendingImages}
          onPendingImagesAction={handlePendingImagesPrimaryAction}
          uploadContext={user && resourceId ? { userId: user.id, resourceId } : undefined}
        />

        {scoperEnabled && scoperContext && (
          <ScoperAiDialog
            open={scoperOpen}
            onClose={() => setScoperOpen(false)}
            context={scoperContext}
          />
        )}

        <UnsplashImageLibraryDialog
          open={libraryOpen}
          onClose={handleCloseLibrary}
          onSelectImage={handleResolvePendingImageFromLibrary}
          initialSearchTerm={librarySearchTerm}
          uploadContext={user && resourceId ? { userId: user.id, resourceId } : undefined}
        />
      </Container>
    </Box>
  );
};

function isPromiseLike(value: unknown): value is Promise<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function'
  );
}

function toSafeFilename(input: string): string {
  const normalized = input
    .trim()
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'document';
}

function isLaterTimestamp(nextValue: string | null, previousValue: string | null): boolean {
  if (!nextValue) return false;
  if (!previousValue) return true;
  const nextTime = Date.parse(nextValue);
  const previousTime = Date.parse(previousValue);
  if (Number.isNaN(nextTime) || Number.isNaN(previousTime)) return false;
  return nextTime > previousTime;
}

function getPaginationLayout(pageSize: PageSize, margins: PageMargins) {
  const base = pageSize === 'a4' ? PAGE_SIZES.A4 : PAGE_SIZES.LETTER;
  const scale = MARGIN_SCALE[margins];

  return {
    pageHeight: base.pageHeight,
    pageWidth: base.pageWidth,
    marginTop: Math.round(base.marginTop * scale),
    marginBottom: Math.round(base.marginBottom * scale),
    marginLeft: Math.round(base.marginLeft * scale),
    marginRight: Math.round(base.marginRight * scale),
  };
}

export default ResourceDocumentEditorPage;
